import React from "react";
import { titleCase } from "../../shared/format";

/**
 * ChangeList — what_changed evidence component.
 *
 * Week-over-week comparison of key metrics. Each metric renders as a compact
 * stat card in a single horizontal row (wraps on narrow screens). The card
 * chrome is neutral (white / gray-900) — colour appears ONLY as a 3px accent
 * strip across the top, mirroring the KpiCard (Google Ads) and IndexTileStrip
 * (Today at the Park) card anatomy. The accent colour reflects polarity:
 * rising is good for some metrics (performance_pct), bad for others
 * (revenue_lost).
 *
 * Spec references: §7 (what_changed).
 */

/**
 * Polarity map — which direction is "good" per metric.
 *  "higher_is_better" → rising = good, falling = bad
 *  "lower_is_better"  → falling = good, rising = bad
 *  "neutral"          → no polarity (direction depends on context)
 * Absent keys are treated as neutral.
 */
const POLARITY = {
  performance_pct: "higher_is_better",
  weather_effect_bookings: "higher_is_better",
  revenue_lost: "lower_is_better",
  revenue_lost_to_conditions: "lower_is_better",
  escape_pressure_f: "higher_is_better",
  comfort_percentile: "higher_is_better",
  bookings_lost: "lower_is_better",
  sessions: "neutral",
};

/**
 * Top-accent border class per polarity outcome.
 * This is the ONLY coloured element on the card — the body stays neutral.
 * Palette mirrors the condition-rating colours used by IndexTileStrip
 * (Today at the Park condition bars) and ActionList priority badges:
 * emerald (much better) / orange (worse) / yellow (typical) / slate (no data).
 */
const ACCENT_BORDER = {
  good: "border-t-emerald-500 dark:border-t-emerald-400",
  bad: "border-t-orange-500 dark:border-t-orange-400",
  neutral: "border-t-yellow-500 dark:border-t-yellow-400",
};

/**
 * Change-indicator text colour by direction.
 * Up (↑) = green, Down (↓) = red.
 */
const DIRECTION_TEXT = {
  up: "text-green-600 dark:text-green-400",
  down: "text-red-600 dark:text-red-400",
};

/**
 * Resolve polarity outcome for a given metric + signed change.
 * Returns "good" | "bad" | "neutral".
 */
const polarityOutcome = (change, metric) => {
  const polarity = POLARITY[metric];
  if (polarity === "neutral" || polarity == null) return "neutral";
  const isPositive = Number(change) >= 0;
  const higherIsBetter = polarity === "higher_is_better";
  const isGood = higherIsBetter ? isPositive : !isPositive;
  return isGood ? "good" : "bad";
};

/**
 * Format a value based on its unit.
 *  "%" → value with % symbol
 *  "$" → currency with commas, 2 decimals
 *  "°F" → value with degree symbol
 *  other → value followed by unit (if any)
 * null/undefined → "—"
 */
const formatValue = (val, unit) => {
  if (val == null) return "—";
  const n = Number(val);
  if (unit === "%") return `${n.toFixed(1)}%`;
  if (unit === "$") {
    return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  if (unit === "°F") return `${n.toFixed(1)}°`;
  return unit ? `${n} ${unit}` : `${n}`;
};

const ChangeList = ({ panel }) => {
  const f = panel.facts || {};

  // Not comparable yet — no data
  if (!f.comparable || !f.changes || f.changes.length === 0) {
    return (
      <p className="text-sm text-gray-400 dark:text-gray-500 italic py-2">
        Not enough data to compare yet.
      </p>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {f.changes.map((item) => {
        const change = Number(item.change);
        const arrow = change >= 0 ? "↑" : "↓";
        const outcome = polarityOutcome(change, item.metric);
        const accent = ACCENT_BORDER[outcome];

        return (
          <div
            key={item.metric}
            className={`flex-1 min-w-[150px] bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 border-t-[3px] ${accent} p-2.5 text-center`}
          >
            <p className="text-[11px] uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400">
              {titleCase(item.label)}
            </p>
            <p className="mt-1.5 text-sm font-bold text-gray-900 dark:text-gray-100">
              {formatValue(item.then, item.unit)} → {formatValue(item.now, item.unit)}
            </p>
            <p className={`text-[11px] font-medium mt-0.5 ${change >= 0 ? DIRECTION_TEXT.up : DIRECTION_TEXT.down}`}>
              {arrow} {formatValue(Math.abs(change), item.unit)}
            </p>
          </div>
        );
      })}
    </div>
  );
};

export default ChangeList;